import { getToken } from '../offline/db';
import type { GeoFix } from '../../platform/types';

/**
 * Route trail recorder — and the app's memory of where the driver has been.
 *
 * This does three jobs, and the second and third are the important ones:
 *
 *  1. Records the breadcrumb trail for the office.
 *  2. Keeps a rolling window of recent fixes, so a completion NEVER depends on
 *     getting a GPS lock at the moment the driver presses the button. Inside a
 *     basement the phone may see nothing; the trail already holds the fix taken
 *     when they walked in from the van, automatically.
 *  3. Detects arrival at a stop by geofence entry, so nobody has to remember to
 *     tap "I've arrived" outside before going in.
 *
 * The design rule throughout: the driver's cooperation is not an input. Anything
 * that would otherwise be an instruction ("tap this before you go inside", "wait
 * for signal") is done by the app instead, or is unnecessary.
 *
 * Recording runs only while a route is in progress; the database independently
 * refuses any point reported outside a live shift.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

/** How often buffered points are shipped. */
const UPLOAD_EVERY_MS = 60_000;
/** Ship early rather than risk losing a long stretch to a killed tab. */
const MAX_BUFFER = 60;
/** Don't store a point for standing still; a stop can last twenty minutes. */
const MIN_MOVE_M = 25;
/** ...but do prove the driver is still there, even when parked. */
const MAX_GAP_MS = 180_000;
/** A fix this vague says nothing useful about where the van was. */
const MAX_ACCURACY_M = 200;

/**
 * How long the driver must be inside a stop's geofence before the app calls it
 * an arrival. Long enough that driving past a hospital on the way somewhere else
 * does not trigger it; short enough that it has already happened by the time
 * they have walked to the door.
 */
const ARRIVAL_DWELL_MS = 40_000;

/** Fixes older than this are no longer evidence of where the driver is now. */
const RECENT_WINDOW_MS = 15 * 60_000;

export interface TrailPoint {
  lat: number;
  lng: number;
  accuracyM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
  recordedAtMs: number;
}

export interface StopTarget {
  taskId: string;
  lat: number;
  lng: number;
  radiusM: number;
}

export function metersBetween(
  a: { lat: number; lng: number }, b: { lat: number; lng: number },
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.sqrt(h));
}

class RouteTracker {
  private watchId: number | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private buffer: TrailPoint[] = [];          // awaiting upload
  private recent: TrailPoint[] = [];          // rolling window for fix selection
  private lastKept: TrailPoint | null = null;
  private token: string | null = null;

  private targets: StopTarget[] = [];
  private insideSince = new Map<string, number>();
  private announced = new Set<string>();
  private arrivalHandler: ((taskId: string) => void) | null = null;

  private onVisibility = () => { void this.flush(); };

  get running(): boolean { return this.watchId !== null; }

  /** Stops the tracker should watch for arrival (pending stops only). */
  setTargets(targets: StopTarget[]): void {
    this.targets = targets;
    // Forget dwell state for stops that are no longer pending, so a re-opened
    // stop can be detected again.
    const ids = new Set(targets.map((t) => t.taskId));
    for (const id of [...this.insideSince.keys()]) {
      if (!ids.has(id)) this.insideSince.delete(id);
    }
  }

  onArrival(handler: (taskId: string) => void): void {
    this.arrivalHandler = handler;
  }

  start(): void {
    if (this.watchId !== null || !('geolocation' in navigator)) return;
    void getToken().then((t) => { this.token = t; }).catch(() => {});

    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this.consider({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracyM: pos.coords.accuracy ?? null,
        speedMps: pos.coords.speed ?? null,
        headingDeg: pos.coords.heading ?? null,
        recordedAtMs: pos.timestamp,
      }),
      () => { /* a gap in the trail is acceptable; a crashed app is not */ },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 30_000 },
    );

    this.timer = setInterval(() => { void this.flush(); }, UPLOAD_EVERY_MS);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  /** Stops recording and ships whatever is left. */
  async stop(): Promise<void> {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.lastKept = null;
    this.insideSince.clear();
    await this.flush();
  }

  /**
   * The best location we can honestly offer for a completion right now.
   *
   * Picks the most accurate fix from the recent window rather than the newest,
   * because the newest may be a vague indoor reading while a sharp one from four
   * minutes ago — when the driver was walking across the car park — describes the
   * same building far better. The server is told the age either way.
   */
  bestRecentFix(maxAgeMs = RECENT_WINDOW_MS): GeoFix | null {
    const cutoff = Date.now() - maxAgeMs;
    let best: TrailPoint | null = null;
    for (const p of this.recent) {
      if (p.recordedAtMs < cutoff) continue;
      const acc = p.accuracyM ?? Number.MAX_SAFE_INTEGER;
      const bestAcc = best?.accuracyM ?? Number.MAX_SAFE_INTEGER;
      if (!best || acc < bestAcc) best = p;
    }
    if (!best) return null;
    return {
      lat: best.lat,
      lng: best.lng,
      accuracyM: best.accuracyM ?? MAX_ACCURACY_M,
      capturedAt: best.recordedAtMs,
    };
  }

  /**
   * The NEWEST point within `maxAgeMs` that the server would accept on
   * accuracy (100 m). For when no fresh fix comes: a recent point beats an
   * old precise one, because the server refuses on age first (31 Oct).
   */
  newestRecentFix(maxAgeMs: number): GeoFix | null {
    const cutoff = Date.now() - maxAgeMs;
    let pick: TrailPoint | null = null;
    for (const p of this.recent) {
      if (p.recordedAtMs < cutoff) continue;
      if (p.accuracyM === null || p.accuracyM > 100) continue;
      if (!pick || p.recordedAtMs > pick.recordedAtMs) pick = p;
    }
    if (!pick) return null;
    return { lat: pick.lat, lng: pick.lng, accuracyM: pick.accuracyM ?? MAX_ACCURACY_M, capturedAt: pick.recordedAtMs };
  }

  /** How many points are still waiting to reach the office. */
  get pendingUploads(): number { return this.buffer.length; }

  private consider(p: TrailPoint): void {
    if (p.accuracyM !== null && p.accuracyM > MAX_ACCURACY_M) return;

    // Rolling window feeds completion fixes and arrival detection; it is kept
    // whether or not the point is worth uploading.
    this.recent.push(p);
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    if (this.recent.length > 200 || (this.recent[0]?.recordedAtMs ?? 0) < cutoff) {
      this.recent = this.recent.filter((r) => r.recordedAtMs >= cutoff).slice(-200);
    }

    this.checkArrival(p);

    // Thin the UPLOAD stream: a parked van producing a fix a second would bury
    // the driving in thousands of identical points.
    if (this.lastKept) {
      const moved = metersBetween(this.lastKept, p);
      const waited = p.recordedAtMs - this.lastKept.recordedAtMs;
      if (moved < MIN_MOVE_M && waited < MAX_GAP_MS) return;
    }
    this.lastKept = p;
    this.buffer.push(p);
    if (this.buffer.length >= MAX_BUFFER) void this.flush();
  }

  /**
   * Geofence entry with dwell. Replaces the driver remembering to tap "I've
   * arrived" outside — which was the single most fragile step in the whole flow,
   * because forgetting it meant the only location available at completion was
   * whatever the phone could scrape together in a basement.
   */
  private checkArrival(p: TrailPoint): void {
    for (const t of this.targets) {
      if (this.announced.has(t.taskId)) continue;
      const inside = metersBetween(p, t) <= t.radiusM;
      if (!inside) { this.insideSince.delete(t.taskId); continue; }

      const since = this.insideSince.get(t.taskId);
      if (since === undefined) {
        this.insideSince.set(t.taskId, p.recordedAtMs);
      } else if (p.recordedAtMs - since >= ARRIVAL_DWELL_MS) {
        this.announced.add(t.taskId);
        this.arrivalHandler?.(t.taskId);
      }
    }
  }

  /**
   * Upload and forget. A failed upload puts the points back so a dead zone costs
   * nothing, capped so a long outage drops the oldest movement rather than
   * growing without limit across a ten-hour shift.
   */
  private async flush(): Promise<void> {
    if (this.buffer.length === 0) return;
    this.token ??= await getToken().catch(() => null);
    if (!this.token) return;

    const points = this.buffer;
    this.buffer = [];

    try {
      const res = await fetch(`${API_BASE}/me/positions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({ points }),
        keepalive: true,          // survives the page being backgrounded
      });
      if (res.status === 401) { this.token = null; this.requeue(points); return; }
      if (!res.ok && res.status >= 500) this.requeue(points);
      // A 4xx means these points will never be accepted (no live route);
      // dropping them is correct.
    } catch {
      this.requeue(points);       // offline
    }
  }

  private requeue(points: TrailPoint[]): void {
    this.buffer = [...points, ...this.buffer].slice(-600);
  }
}

/**
 * One instance for the whole app: the stop screen reads the same rolling window
 * the route screen is filling, which is what lets a completion use a fix nobody
 * had to ask for.
 */
export const routeTracker = new RouteTracker();
